import { Badge } from "react-bootstrap"
import ButtonControl from "../../components/ButtonControl"
import CAccordion from "../../components/CAccordion"
import InputItem from "../../components/InputItem"
import Label from "../../components/Label"
import { IPBannedEntry } from "../../api/ip/data"

interface IPBannedItemProps {
    entry: IPBannedEntry
    onUnban?: (ip: string, reason: string) => void
    onEdit?: (entry: IPBannedEntry) => void
}

const IPBannedItem = ({
    entry,
    onUnban = (ip: string, reason: string) => { },
    onEdit = (entry: IPBannedEntry) => { },
}: IPBannedItemProps) => {
    const formatDate = (dateString: string) => {
        const date = new Date(dateString)
        return date.toLocaleString()
    }

    const getReasonBadge = (reason: string) => {
        switch (reason) {
            case 'credential_stuffing': return <Badge bg="danger">Credential Stuffing</Badge>
            case 'expired_session': return <Badge bg="danger">Expired Session Abuse</Badge>
            case 'failed_password': return <Badge bg="warning" text="dark">Failed Password</Badge>
            case 'abuse': return <Badge bg="danger">Abuse</Badge>
            case 'fraud_suspected': return <Badge bg="danger">Fraud Suspected</Badge>
            case 'admin_override': return <Badge bg="info">Admin Manual Ban</Badge>
            default: return <Badge bg="warning" text="dark">Rate Limiting</Badge>
        }
    }

    const getTimeRemaining = (expiresAt: string) => {
        if (!expiresAt || expiresAt.startsWith("0001")) return "Permanent"

        const now = new Date()
        const expires = new Date(expiresAt)
        const diff = expires.getTime() - now.getTime()

        if (diff <= 0) return "Expired"

        const hours = Math.floor(diff / (1000 * 60 * 60))
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

        if (hours > 0) {
            return `${hours}h ${minutes}m remaining`
        }
        return `${minutes}m remaining`
    }

    const getLabel = (reason: string): string => {
        switch (reason) {
            case 'credential_stuffing': return `${entry.ip} [Credential Stuffing]`
            case 'expired_session': return `${entry.ip} [Expired Session Abuse]`
            case 'failed_password': return `${entry.ip} [Failed Password]`
            case 'abuse': return `${entry.ip} [Abuse]`
            case 'fraud_suspected': return `${entry.ip} [Fraud]`
            case 'admin_override': return `${entry.ip} [Admin Ban]`
            default: return `${entry.ip} [Rate Limiting]`
        }
    }

    return <CAccordion label={getLabel(entry.reason)}>
        <InputItem edit={false} text="IP Address" value={entry.ip} />
        <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
            <div style={{ flex: "0 0 33.333%", paddingRight: "10px" }}>
                <Label>Ban Reason</Label>
            </div>
            <div style={{ flex: "1" }}>
                {getReasonBadge(entry.reason)}
            </div>
        </div>
        <InputItem edit={false} text="Banned At" value={formatDate(entry.bannedAt)} />
        <InputItem edit={false} text="Expires At" value={(!entry.expiresAt || entry.expiresAt.startsWith("0001")) ? "Permanent" : `${formatDate(entry.expiresAt)} (${getTimeRemaining(entry.expiresAt)})`} />
        {entry.bannedBy &&
            <InputItem edit={false} text="Banned By" value={entry.bannedBy} />
        }
        <InputItem edit={false} text="Attempt Count" value={entry.attemptCount.toString()} />
        {entry.clientId &&
            <InputItem edit={false} text="Client ID" value={entry.clientId} />
        }
        {entry.appId &&
            <InputItem edit={false} text="App ID" value={entry.appId} />
        }
        {entry.blockedUsers && entry.blockedUsers.length > 0 &&
            <InputItem edit={false} text="Blocked Users" value={entry.blockedUsers.join(", ")} />
        }
        <ButtonControl
            labelColor="orange"
            text="Unban IP Address"
            variant="outline-warning"
            buttonLabel="Unban"
            onClicked={() => onUnban(entry.ip, entry.reason)}
        />
        <ButtonControl
            labelColor="blue"
            text="Edit Ban Configuration"
            variant="outline-primary"
            buttonLabel="Edit"
            onClicked={() => onEdit(entry)}
        />
    </CAccordion>
}

export default IPBannedItem